import axios from 'axios';
import log from 'loglevel';

const paths = {
  csrf: '/api/v1/security/csrf',
  
  me: '/api/v1/users/me/',
  changePassword: '/api/v1/users/change-password/',
  
  registerCheck: '/api/v1/auth/register/check/',
  register: '/api/v1/auth/register/',
  login: '/api/v1/auth/login/',
  logout: '/api/v1/auth/logout/',
  refresh: '/api/v1/auth/refresh/',
  oauthSession: '/api/v1/auth/oauth-session/',    
  oauthContext: '/api/v1/auth/oauth/context/',
  oauthComplete: '/api/v1/auth/oauth/complete/',
  
  invite: '/api/v1/auth/invite/complete/',
  verificationCodes: '/api/v1/auth/verification-codes/',
  findAccounts: '/api/v1/auth/accounts/find/',
  passwordResetToken: '/api/v1/auth/password/reset-tokens/',
  resetPassword: '/api/v1/auth/password/reset/',

  practioners: '/api/v1/practitioners/',
  
  posts: '/api/v1/posts/',
  post: postId => `/api/v1/posts/${postId}/`,
  postView: postId => `/api/v1/posts/${postId}/views/`,
  postComments: postId => `/api/v1/posts/${postId}/comments/`,
  comment: commentId => `/api/v1/comments/${commentId}/`,
  commentChildren: commentId => `/api/v1/comments/${commentId}/children/`,
  postVote: postId => `/api/v1/posts/${postId}/votes/`,
  postHeart: postId => `/api/v1/posts/${postId}/hearts/`,
  commentVote: commentId => `/api/v1/comments/${commentId}/votes/`,
  commentHeart: commentId => `/api/v1/comments/${commentId}/hearts/`,
  temporaryImage: '/api/v1/media/temporary-images/',
};

const axiosInstance = axios.create({
  withCredentials: true,
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken',
});

const client = {
  refreshPromise: null,
  
  async get(path, config={}) {
    return await axiosInstance.get(path, config);
  },

  async post(path, data={}, config={}) {
    return await axiosInstance.post(path, data, config);
  },

  async patch(path, data={}, config={}) {
    return await axiosInstance.patch(path, data, config);
  },

  async delete(path, config={}) {
    return await axiosInstance.delete(path, config);
  },

  async _sRequest(method, path, data=undefined, config={}, tried={}) {
    log.trace(`WookAxios secure ${method}`);

    try {
      return await axiosInstance.request({method, url: path, data, ...config});
    } catch (e) {
      if (client.isCSRFError(e) && !tried.csrf) {
        tried.csrf = true;
        log.debug('CSRF_FAILED trying refresh csrftoken');
        const csrf_response = await client.csrf();
        log.debug('CSRF_REFRESH_SUCEESS', csrf_response);
        return await client._sRequest(method, path, data, config, { ...tried });
      }

      if (e.response?.status === 401 && !tried.refresh_token) {
        tried.refresh_token = true;
        log.debug('AXIOS_AUTH_FAILED trying refresh token')
        const refresh_response = await client.refresh();
        log.debug('AXIOS_REFRESH_TOKEN_SUCCESS', refresh_response);
        return  await client._sRequest(method, path, data, config, { ...tried });
      } else {
        throw e;
      }
    }     
  },

  isCSRFError(error) {
    const status = error.response?.status;
    if (status !== 403) return false;

    const errors = error.response?.data.errors;
    if (!errors) return false;

    const CSRFErrors = ['CSRF_TOKEN_MISSING', 'CSRF_TOKEN_INVALID'];
    const isCSRFError = Object.values(errors).flat().some(msg => CSRFErrors.includes(msg));
    return isCSRFError;
  },

  async sGet(path, config) {
    return client._sRequest('get', path, undefined, config);
  },

  async sPost(path, data, config) {
    return client._sRequest('post', path, data, config);
  },

  async sPatch(path, data, config) {
    return client._sRequest('patch', path, data, config);
  },

  async sDelete(path, config) {
    return client._sRequest('delete', path, undefined, config);
  },

  async csrf(path=paths.csrf) {
    log.trace('WookAxios csrf');
    return await client.get(path);
  },  

  async me(path=paths.me) {
    log.trace('WookAxios me');
    return await client.sGet(path);
  },

  async updateMe(data, path=paths.me) {
    log.trace('WookAxios update me');
    return await client.sPatch(path, data);
  },

  async changePassword(data, path=paths.changePassword) {
    log.trace('WookAxios change password');
    return await client.sPost(path, data);
  },

  async registerCheck(data, path=paths.registerCheck) {
    log.trace('WookAxios register check');
    return await axiosInstance.post(path, data);
  },

  async register(data, provider=null) {
    log.trace('WookAxios register');
    const oauthPath = provider ? `${provider}/` : '';
    const path = paths.register + oauthPath;
    return await axiosInstance.post(path, data);
  },

  async login(authData, provider=null) {
    log.trace('WookAxios login');
    const oauthPath = provider ? `${provider}/` : '';
    const path = paths.login + oauthPath;
    return await axiosInstance.post(path, authData);
  },

  async logout(path=paths.logout) {
    log.trace('WookAxios logout');
    return await client.sPost(path);
  },

  async resign(path=paths.me) {
    log.trace('WookAxios quit');
    return await client.sDelete(path);
  },

  async refresh(path=paths.refresh) {
    log.trace('WookAxios refresh');

    if (client.refreshPromise) {
      return client.refreshPromise;
    }

    const goRefresh = async () => {
      return await axiosInstance.post(path);
    };

    client.refreshPromise = goRefresh();

    try {
      return await client.refreshPromise;
    } finally {
      client.refreshPromise = null;
    }
  },

  async oauthSession(path=paths.oauthSession) {
    log.trace('WookAxios oauth session');
    return await client.sGet(path);
  },
  
  async oauthContext(path=paths.oauthContext) {
    log.trace('WookAxios oauth context');
    return await client.post(path);
  },
  
  async oauthComplete(data, path=paths.oauthComplete) {
    log.trace('WookAxios oauth complete');
    return await client.post(path, data);
  },

  async invite(data, path=paths.invite) {
    log.trace('WookAxios invite');
    return await client.post(path, data);
  },

  async verificationCodes(data, path=paths.verificationCodes) {
    log.trace('WookAxios verify email');
    return await client.post(path, data);
  },

  async findAccounts(data, path=paths.findAccounts) {
    log.trace('WookAxios find accounts');
    return await client.post(path, data);
  },

  async passwordResetToken(data, path=paths.passwordResetToken) {
    log.trace('WookAxios password reset token');
    return await client.post(path, data);
  },  

  async resetPassword(data, path=paths.resetPassword) {
    log.trace('WookAxios reset password');
    return await client.post(path, data);
  },

  async getPractitioners(query, path=paths.practioners) {
    log.trace('WookAxios get practioners');
    const params = { params: { query }};
    return client.get(path, params);
  },
  
  async getPostsPage(page=1, path=paths.posts) {
    log.trace('WookAxios get posts page');
    const params = { params: { page }};
    return await client.sGet(path, params);
  },

  async getPost(postId, path=paths.post) {
    log.trace('WookAxios get post');
    return await client.sGet(path(postId));
  },

  async getPostComments(postId, page, order, path=paths.postComments) {
    log.trace('WookAxios get post comments');    
    const config = { params: { page, order }};
    return await client.sGet(path(postId), config);
  },
  
  async getCommentChildren(commentId, page, path=paths.commentChildren) {
    log.trace('WookAxios get comment children');
    const config = { params: { page } };
    return await client.sGet(path(commentId), config);
  },

  async deletePost(postId, path=paths.post) {
    log.trace('WookAxios delete post');
    return await client.sDelete(path(postId));
  },

  async increasePostView(postId, path=paths.postView) {
    log.trace('WookAxios increase post view');
    return await client.sPost(path(postId));
  },

  async createComment(postId, data, path=paths.postComments) {
    log.trace('WookAxios create comment');
    return await client.sPost(path(postId), data);
  },
  
  async updateComment(commentId, data, path=paths.comment) {
    log.trace('WookAxios update comment');
    return await client.sPatch(path(commentId), data);
  },

  async deleteComment(commentId, path=paths.comment) {
    log.trace('WookAxios delete comment');
    return await client.sDelete(path(commentId));
  },

  async submitPostVote(postId, data, path=paths.postVote) {
    log.trace('WookAxios submit post vote');
    return await client.sPost(path(postId), data);
  },

  async deletePostVote(postId, path=paths.postVote) {
    log.trace('WookAxios delete post vote');
    return await client.sDelete(path(postId));
  },

  async createPostHeart(postId, path=paths.postHeart) {
    log.trace('WookAxios create post heart');
    return await client.sPost(path(postId));
  },

  async deletePostHeart(postId, path=paths.postHeart) {
    log.trace('WookAxios delete post heart');
    return await client.sDelete(path(postId));
  },

  async submitCommentVote(commentId, data, path=paths.commentVote) {
    log.trace('WookAxios submit comment vote');
    return await client.sPost(path(commentId), data);
  },

  async deleteCommentVote(commentId, path=paths.commentVote) {
    log.trace('WookAxios delete comment vote');
    return await client.sDelete(path(commentId));
  },

  async createCommentHeart(commentId, path=paths.commentHeart) {
    log.trace('WookAxios create comment heart');
    return await client.sPost(path(commentId));
  },

  async deleteCommentHeart(commentId, path=paths.commentHeart) {
    log.trace('WookAxios delete comment heart');
    return await client.sDelete(path(commentId));
  },

  async uploadTemporaryImage(data, path=paths.temporaryImage) {
    log.trace('WookAxios upload temporary image');
    return await client.sPost(path, data);
  },
}

export default client;